// animations/hover-animations.ts
import gsap from 'gsap';

import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';
import { baseAppear, baseFade, baseMove, baseRotate, baseSkew, baseTwist } from './common-base-animations';
import { makeHoverTween } from './dot-cms-aniamtion.helper';

export const hoverAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts?: gsap.TweenVars) => gsap.core.Tween>
> = {
  'hover-expand': (s, el, opts) =>
    makeHoverTween(el, () =>
      gsap.to(el, {
        scale: s.scale ?? 1.2,
        duration: s.duration ?? 0.3,
        ease: s.ease ?? 'power2.out',
        transformOrigin: s.origin ?? 'center center',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-shrink': (s, el, opts) =>
    makeHoverTween(el, () =>
      gsap.to(el, {
        scale: s.scale ?? 0.85,
        duration: s.duration ?? 0.3,
        ease: s.ease ?? 'power2.out',
        transformOrigin: s.origin ?? 'center center',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-skew': (s, el, opts) =>
    makeHoverTween(el, () =>
      baseSkew(el, {
        skewX: s.angle ?? 15,
        duration: s.duration ?? 0.3,
        ease: s.ease ?? 'power2.out',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-rotate': (_s, el, opts) =>
    makeHoverTween(el, () =>
      baseRotate(el, {
        rotation: '+=360',
        duration: _s.duration ?? 0.5,
        ease: _s.ease ?? 'power2.out',
        transformOrigin: _s.origin ?? 'center center',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-move': (s, el, opts) =>
    makeHoverTween(el, () => {
      const rad = ((s.angle ?? 0) * Math.PI) / 180;
      return baseMove(el, {
        x: Math.cos(rad) * 40,
        y: Math.sin(rad) * 40,
        scale: s.scale ?? 1,
        duration: s.duration ?? 0.4,
        ease: s.ease ?? 'power2.out',
        overwrite: 'auto',
        ...opts,
      });
    }),

  'hover-twist': (s, el, opts) =>
    makeHoverTween(el, () =>
      baseTwist(el, {
        rotation: s.angle ?? 180,
        scale: s.scale ?? 1,
        duration: s.duration ?? 0.5,
        ease: s.ease ?? 'power2.out',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-fade': (s, el, opts) =>
    makeHoverTween(el, () =>
      baseFade(el, {
        opacity: s.opacity ?? 0.3,
        duration: s.duration ?? 0.4,
        ease: s.ease ?? 'power2.out',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-appear': (_s, el, opts) =>
    makeHoverTween(el, () =>
      baseAppear(el, {
        duration: _s.duration ?? 0.4,
        ease: _s.ease ?? 'power2.out',
        overwrite: 'auto',
        ...opts,
      })
    ),

  'hover-color': (s, el, opts) =>
    makeHoverTween(el, () => {
      const colorValue = s.fillColor ?? '#ff4081'; // default to pink accent
      const isSVG = el instanceof SVGElement;
      const prop = isSVG ? 'fill' : 'color';
      return gsap.to(el, {
        [prop]: colorValue,
        duration: s.duration ?? 0.4,
        ease: s.ease ?? 'power2.out',
        overwrite: 'auto',
        ...opts,
      });
    }),
};
